import { useState, useEffect } from 'react';
import { Box, Typography, TextField, Button, Link as MuiLink, LinearProgress, Alert, Stack, InputAdornment, IconButton } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useNavigate, useLocation } from 'react-router-dom';
import { Eye, EyeOff, ArrowLeft, CheckCircle, XCircle } from 'lucide-react';

const PageContainer = styled(Box)(() => ({
  minHeight: '100vh',
  display: 'grid',
  gridTemplateColumns: '1fr',
  '@media (min-width: 1024px)': {
    gridTemplateColumns: '1fr 1fr'
  }
}));

const LeftPanel = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '2.5rem 1rem',
  backgroundColor: 'white',
  '@media (min-width: 640px)': {
    padding: '4rem 1.5rem'
  },
  '@media (min-width: 1024px)': {
    padding: '6rem 2rem'
  }
}));

const RightPanel = styled(Box)(() => ({
  display: 'none',
  '@media (min-width: 1024px)': {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '4rem 2rem',
    backgroundColor: '#F9FAFB'
  }
}));

const StyledTextField = styled(TextField)(() => ({
  '& .MuiOutlinedInput-root': {
    backgroundColor: '#F9FAFB',
    borderRadius: '8px',
    '& input': {
      padding: '16px'
    },
    '& fieldset': {
      borderColor: '#E5E7EB'
    },
    '&:hover fieldset': {
      borderColor: '#D1D5DB'
    },
    '&.Mui-focused': {
      backgroundColor: 'white',
      '& fieldset': {
        borderColor: '#4F46E5',
        borderWidth: '1px'
      }
    }
  },
  '& .MuiInputBase-input::placeholder': {
    color: '#9CA3AF',
    opacity: 1
  }
}));

const PrimaryButton = styled(Button)(() => ({
  width: '100%',
  padding: '16px',
  backgroundColor: '#4F46E5',
  color: 'white',
  fontWeight: 600,
  fontSize: '1rem',
  textTransform: 'none',
  borderRadius: '8px',
  boxShadow: 'none',
  '&:hover': {
    backgroundColor: '#4338CA',
    boxShadow: 'none'
  },
  '&:disabled': {
    backgroundColor: '#D1D5DB',
    color: '#9CA3AF'
  }
}));


interface PasswordStrength {
  score: number;
  label: string;
  color: string;
  percentage: number;
}

const calculatePasswordStrength = (password: string): PasswordStrength => {
  let score = 0;
  
  if (password.length >= 8) score++;
  if (/[A-Z]/.test(password)) score++;
  if (/[0-9]/.test(password)) score++;
  if (/[^A-Za-z0-9]/.test(password)) score++;

  if (score === 0 || score === 1) {
    return { score, label: 'Weak', color: '#ef4444', percentage: 25 };
  } else if (score === 2 || score === 3) {
    return { score, label: 'Medium', color: '#f59e0b', percentage: 66 };
  } else {
    return { score, label: 'Strong', color: '#22c55e', percentage: 100 };
  }
};

const ResetPassword = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { email, token } = location.state || {};

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Password strength
  const [passwordStrength, setPasswordStrength] = useState<PasswordStrength>({
    score: 0,
    label: 'Weak',
    color: '#ef4444',
    percentage: 0
  });

  // Password requirements
  const [requirements, setRequirements] = useState({
    minLength: false,
    hasUppercase: false,
    hasNumber: false,
    hasSpecial: false
  });

  // Carousel state
  const [currentSlide, setCurrentSlide] = useState(0);
  const slides = [
    {
      image: 'https://images.unsplash.com/photo-1633265486064-086b219458ec?w=800&auto=format&fit=crop',
      title: 'Create a Strong Password',
      description: 'Protect your business account with a secure password. Make it unique and memorable.'
    },
    {
      image: 'https://images.unsplash.com/photo-1563013544-824ae1b704d3?w=800&auto=format&fit=crop',
      title: 'Security First',
      description: 'Your business data is precious. We help you keep it safe with industry-standard security practices.'
    },
    {
      image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&auto=format&fit=crop',
      title: 'Back in Business',
      description: 'Once you reset your password, you\'ll be back to managing reviews and growing your reputation.'
    }
  ];

  // Auto-advance carousel
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000);

    return () => clearInterval(timer);
  }, [slides.length]);

  // Update password strength and requirements
  useEffect(() => {
    if (newPassword) {
      setPasswordStrength(calculatePasswordStrength(newPassword));
      setRequirements({
        minLength: newPassword.length >= 8,
        hasUppercase: /[A-Z]/.test(newPassword),
        hasNumber: /[0-9]/.test(newPassword),
        hasSpecial: /[^A-Za-z0-9]/.test(newPassword)
      });
    } else {
      setPasswordStrength({ score: 0, label: 'Weak', color: '#ef4444', percentage: 0 });
      setRequirements({
        minLength: false,
        hasUppercase: false,
        hasNumber: false,
        hasSpecial: false
      });
    }
  }, [newPassword]);

  // Redirect if no email/token
  useEffect(() => {
    if (!email || !token) {
      setError('Invalid reset link. Please start the password reset process again.');
      navigate('/forgot-password');
    }
  }, [email, token, navigate]);

  const handleResetPassword = async () => {
    setError(null);
    setSuccess(null);

    // Validation
    if (!newPassword) {
      setError('Please enter a new password');
      return;
    }

    if (!confirmPassword) {
      setError('Please confirm your password');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    // Check all requirements
    if (!requirements.minLength || !requirements.hasUppercase || !requirements.hasNumber || !requirements.hasSpecial) {
      setError('Password does not meet all requirements');
      return;
    }

    setIsLoading(true);

    try {
      // TODO: Call reset password API
      console.log('Resetting password for:', email, 'with token:', token);
      
      setSuccess('Password reset successfully! Redirecting to sign in...');
      
      // Navigate to sign in page
      navigate('/signin', {
        state: {
          message: 'Password reset successfully! Please sign in with your new password.'
        }
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reset password. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const passwordsMatch = confirmPassword && newPassword === confirmPassword;
  const passwordsDontMatch = confirmPassword && newPassword !== confirmPassword;

  return (
    <PageContainer>
      <LeftPanel>
        <Box sx={{ width: '100%', maxWidth: 448, mx: 'auto' }}>
          {/* Header */}
          <Typography
            sx={{
              fontSize: { xs: '1.875rem', sm: '2.25rem' },
              fontWeight: 700,
              lineHeight: 1.2,
              color: '#111827',
              mb: 1
            }}
          >
            Set New Password
          </Typography>
          <Typography sx={{ fontSize: '1rem', color: '#6B7280', mb: 4 }}>
            Create a strong password to protect your account and business data.
          </Typography>

          {/* Error Alert */}
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {/* Success Alert */}
          {success && (
            <Alert severity="success" sx={{ mb: 3 }}>
              {success}
            </Alert>
          )}

          <Stack spacing={3}>
            {/* New Password Field */}
            <Box>
              <Typography
                component="label"
                sx={{
                  display: 'block',
                  fontSize: '1rem',
                  fontWeight: 500,
                  color: '#111827',
                  mb: 1.25
                }}
              >
                New Password
              </Typography>
              <StyledTextField
                fullWidth
                type={showNewPassword ? 'text' : 'password'}
                placeholder="Create a strong password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                disabled={isLoading}
                autoFocus
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        edge="end"
                        sx={{ color: '#6B7280', mr: 0.5 }}
                      >
                        {showNewPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
            </Box>

            {/* Password Strength Indicator */}
            {newPassword && (
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                  <Typography sx={{ fontSize: '0.875rem', fontWeight: 500, color: '#111827' }}>
                    Password Strength
                  </Typography>
                  <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: passwordStrength.color }}>
                    {passwordStrength.label}
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={passwordStrength.percentage}
                  sx={{
                    height: 8,
                    borderRadius: 4,
                    backgroundColor: '#E5E7EB',
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: passwordStrength.color,
                      borderRadius: 4
                    }
                  }}
                />
              </Box>
            )}

            {/* Password Requirements Checklist */}
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                gap: 1.5
              }}
            >
              {[
                { key: 'minLength', label: 'At least 8 characters' },
                { key: 'hasUppercase', label: 'One uppercase letter' },
                { key: 'hasNumber', label: 'At least one number' },
                { key: 'hasSpecial', label: 'One special character' }
              ].map(({ key, label }) => (
                <Box
                  key={key}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                  }}
                >
                  {requirements[key as keyof typeof requirements] ? (
                    <CheckCircle size={18} color="#22C55E" />
                  ) : (
                    <XCircle size={18} color="#D1D5DB" />
                  )}
                  <Typography
                    sx={{
                      fontSize: '0.875rem',
                      color: requirements[key as keyof typeof requirements] ? '#111827' : '#9CA3AF'
                    }}
                  >
                    {label}
                  </Typography>
                </Box>
              ))}
            </Box>

            {/* Confirm Password Field */}
            <Box>
              <Typography
                component="label"
                sx={{
                  display: 'block',
                  fontSize: '1rem',
                  fontWeight: 500,
                  color: '#111827',
                  mb: 1.25
                }}
              >
                Confirm New Password
              </Typography>
              <StyledTextField
                fullWidth
                type={showConfirmPassword ? 'text' : 'password'}
                placeholder="Re-enter your password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                disabled={isLoading}
                error={passwordsDontMatch}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        edge="end"
                        sx={{ color: '#6B7280', mr: 0.5 }}
                      >
                        {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      borderColor: passwordsDontMatch ? '#EF4444' : '#E5E7EB'
                    },
                    '&:hover fieldset': {
                      borderColor: passwordsDontMatch ? '#EF4444' : '#D1D5DB'
                    }
                  }
                }}
              />
              {passwordsDontMatch && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
                  <XCircle size={16} color="#EF4444" />
                  <Typography sx={{ fontSize: '0.875rem', color: '#EF4444' }}>
                    Passwords do not match
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Reset Button */}
            <PrimaryButton
              onClick={handleResetPassword}
              disabled={isLoading || !passwordsMatch || !Object.values(requirements).every(Boolean)}
            >
              {isLoading ? 'Resetting Password...' : 'Reset Password'}
            </PrimaryButton>

            {/* Back to Sign In */}
            <Box sx={{ textAlign: 'center' }}>
              <MuiLink
                component="button"
                onClick={() => navigate('/signin')}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.5,
                  color: '#6B7280',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  cursor: 'pointer',
                  textDecoration: 'none',
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  '&:hover': {
                    color: '#4F46E5'
                  }
                }}
              >
                <ArrowLeft size={16} />
                Back to Sign In
              </MuiLink>
            </Box>
          </Stack>
        </Box>
      </LeftPanel>

      <RightPanel>
        {/* Carousel */}
        <Box sx={{ width: '100%', maxWidth: 600, position: 'relative' }}>
          {/* Carousel Image */}
          <Box
            component="img"
            src={slides[currentSlide].image}
            alt={slides[currentSlide].title}
            sx={{
              width: '100%',
              height: 400,
              objectFit: 'cover',
              borderRadius: '16px',
              mb: 3
            }}
          />

          {/* Carousel Text */}
          <Box sx={{ textAlign: 'center', px: 2 }}>
            <Typography
              sx={{
                fontSize: '1.5rem',
                fontWeight: 700,
                color: '#111827',
                mb: 2
              }}
            >
              {slides[currentSlide].title}
            </Typography>
            <Typography sx={{ fontSize: '1rem', color: '#6B7280', lineHeight: 1.6 }}>
              {slides[currentSlide].description}
            </Typography>
          </Box>

          {/* Carousel Indicators */}
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mt: 4 }}>
            {slides.map((_, index) => (
              <Box
                key={index}
                onClick={() => setCurrentSlide(index)}
                sx={{
                  width: currentSlide === index ? 32 : 8,
                  height: 8,
                  borderRadius: 4,
                  backgroundColor: currentSlide === index ? '#4F46E5' : '#E5E7EB',
                  cursor: 'pointer',
                  transition: 'all 0.3s'
                }}
              />
            ))}
          </Box>
        </Box>
      </RightPanel>
    </PageContainer>
  );
};

export default ResetPassword;

